import React, { useContext, useState } from 'react';

import { CombatContext } from '../contexts/CombatContext';
import { runtimeContextPayload } from './combatContextIdentity';
import { ensureSharingContext } from './sharingApi';
import { createAfterActionPacket, sendAfterActionHandoff } from './combatProRuntimeApi';
import { generatorCommerceRoute, launchGeneratorInDcc } from './dccGeneratorLaunch';
import { openSpecialistSource } from './provenanceApi';
import { useNavigate } from 'react-router-dom';
import { ActionPill, StatusBadge } from './ui/LayeredWorkspace';

function readable(value) { return String(value || '').replace(/_/g, ' '); }

export default function AfterActionHandoffPanel({ afterAction }) {
  const combat = useContext(CombatContext);
  const navigate = useNavigate();
  const [packet, setPacket] = useState(null);
  const [destinations, setDestinations] = useState({});
  const [destination, setDestination] = useState('mission-report-system');
  const [selected, setSelected] = useState([]);
  const [handoff, setHandoff] = useState(null);
  const [notice, setNotice] = useState('Save only when this encounter outcome is ready for explicit handoff.');
  const facts = (afterAction && afterAction.facts) || {};

  async function savePacket() {
    try {
      const identity = await ensureSharingContext(runtimeContextPayload(combat.state));
      const result = await createAfterActionPacket({ campaign_id: identity.campaign_id, encounter_id: identity.encounter_id, facts: facts });
      setPacket(result.packet); setDestinations(result.destinations || {}); setSelected(Object.keys(facts));
      setNotice('Structured encounter facts saved. Choose a destination and relevant facts.');
    } catch (error) { setNotice('The structured after-action packet could not be saved.'); }
  }

  function toggle(key) {
    setSelected(function (current) { return current.indexOf(key) >= 0 ? current.filter(function (value) { return value !== key; }) : current.concat([key]); });
  }

  async function send() {
    try {
      const result = await sendAfterActionHandoff(packet.id, { destination: destination, selected_fact_keys: selected });
      setPacket(result.packet); setHandoff(result.handoff);
      setNotice('Selected true facts are ready for ' + result.handoff.destination_name + '. No consequence programme was generated.');
    } catch (error) { setNotice('Select at least one fact from this packet before handoff.'); }
  }

  async function openDestination() {
    if (!handoff) return;
    if (!handoff.owned) {
      navigate(generatorCommerceRoute(handoff.destination, 'info'));
      return;
    }
    try {
      const authorization = await openSpecialistSource(handoff.destination);
      await launchGeneratorInDcc(authorization);
      setNotice('Destination generator opened in DCC.');
    } catch (error) {
      setNotice(error && error.message === 'dcc_required' ? 'Open this destination in the VISINVOKE Desktop Control Centre.' : 'The destination is not owned, available or authorised for this DCC session.');
    }
  }

  return <div className="cp-after-action-handoff">
    <p role="status">{notice}</p>
    {!packet ? <ActionPill primary disabled={!Object.keys(facts).length} onClick={savePacket}>Save structured outcome</ActionPill> : <>
      <div className="cp-after-action-facts" aria-label="Select relevant after-action facts">
        {Object.keys(packet.facts || {}).map(function (key) { return <label key={key}><input type="checkbox" checked={selected.indexOf(key) >= 0} onChange={function () { toggle(key); }} /> {readable(key)}</label>; })}
      </div>
      <div className="cp-action-row">
        <select aria-label="After-action destination" value={destination} onChange={function (event) { setDestination(event.target.value); }}>
          {Object.keys(destinations).map(function (slug) { return <option value={slug} key={slug}>{destinations[slug]}</option>; })}
        </select>
        <ActionPill primary disabled={!selected.length} onClick={send}>Send / use this outcome</ActionPill>
      </div>
    </>}
    {handoff ? <div className="cp-after-action-ready"><StatusBadge tone="ready">READY FOR DESTINATION</StatusBadge><p>{handoff.selected_fact_keys.length} selected fact groups.</p><ActionPill onClick={openDestination}>{handoff.owned ? 'Open destination generator' : 'More info / buy'}</ActionPill></div> : null}
  </div>;
}
